import { Info } from 'lucide-react';
import _ from 'lodash';
import { connect } from 'react-redux';
import React, { useEffect } from 'react';

import { Actions } from '../../store/actions/facilityActions';
import FacilityMetricRow from './FacilityMetricRow';

import { Button } from '../ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '../ui/card';
import { Table, TableHead, TableHeader, TableRow, TableBody, TableCell } from '../ui/table';

const FacilityMetrics = ({
  list,
  facMetrics,
  addFacilityMetric,
  branding,
  getFacilityMetrics,
  currentOrganisation,
  facility,
  canView,
  canCreate,
}) => {
  // const { primaryColor } = branding.colors || {};

  useEffect(() => {
    if (canView && currentOrganisation) {
      getFacilityMetrics();
    }
  }, [canView, currentOrganisation, getFacilityMetrics]);

  if (!canView) return null;

  return (
    <Card className='border rounded-md'>
      <CardHeader className='flex flex-row items-center justify-between pb-4'>
        <CardTitle className='text-xl font-semibold uppercase'>Your Facility Metrics</CardTitle>
      </CardHeader>
      <CardContent>
        <Table id='metric-table' wrapperClassName='overflow-visible'>
          <TableHeader>
            <TableRow className='text-nowrap'>
              <TableHead>Reporting Period</TableHead>
              {facMetrics?.metric1 && (
                <TableHead id='orgMetric1'>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        {facMetrics.metric1?.name} <Info size={14} strokeWidth={1.5} className='cursor-pointer ml-1 text-primary inline' />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>{facMetrics.metric1?.description}</TooltipContent>
                  </Tooltip>
                </TableHead>
              )}
              {facMetrics?.metric2 && (
                <TableHead id='orgMetric2'>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        {facMetrics.metric2?.name} <Info size={14} strokeWidth={1.5} className='cursor-pointer ml-1 text-primary inline' />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>{facMetrics.metric2?.description}</TooltipContent>
                  </Tooltip>
                </TableHead>
              )}
              {facMetrics?.metric3 && (
                <TableHead id='orgMetric3'>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        {facMetrics.metric3?.name} <Info size={14} strokeWidth={1.5} className='cursor-pointer ml-1 text-primary inline' />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>{facMetrics.metric3?.description}</TooltipContent>
                  </Tooltip>
                </TableHead>
              )}
              <TableHead className='text-right'></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody id='facMetrics-Rows'>
            {list && Array.isArray(list) && list.length > 0 ? (
              list.map((metric, index) => (
                <FacilityMetricRow key={index} metric={metric} facMetrics={facMetrics} list={list} />
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} className='text-center'>
                  No metrics available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
      {canCreate && (
        <CardFooter className='flex justify-start pt-2 pb-4'>
          <Button id='add-a-metric-button' onClick={addFacilityMetric}>
            Add a Metric
          </Button>
        </CardFooter>
      )}
    </Card>
  );
};

const mapStateToProps = (state, ownProps) => {
  const facilityID = ownProps.id;

  const organisation = state?.organisation?.details;
  const metrics = state?.metric?.list;
  const industries = state?.organisation?.industries;
  const orgIndustry = _.find(industries, { id: organisation?.industry });

  const facMetrics = {};

  if (metrics && organisation) {
    facMetrics.metric1 = metrics?.find((item) => item?.name === 'm2');
    facMetrics.metric2 = metrics?.find((item) => item?.name === 'FTE');

    if (orgIndustry?.intensityRatioMetric1ID) {
      const metric = metrics?.find((item) => item?.id === orgIndustry?.intensityRatioMetric1ID);

      if (!['currency', 'm2', 'FTE'].includes(metric?.name)) {
        facMetrics.metric3 = metric;
      }
    }
  }

  return {
    branding: state?.branding,
    list: _.uniqBy(
      state?.facility?.metrics?.filter((item) => item?.facility === facilityID),
      'id',
    ),
    metrics: state?.facility?.metrics?.filter((item) => item?.facility === facilityID),
    facMetrics,
    currentOrganisation: state.currentOrganisation,
    canView: !!state?.organisation?.permissions?.facilityMetrics?.view,
    canCreate: !!state?.organisation?.permissions?.facilityMetrics?.create,
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  addFacilityMetric: () => dispatch(Actions.addFacilityMetric(ownProps?.id)),
  getFacilityMetrics: () => dispatch(Actions.getFacilityMetrics()),
});

export default connect(mapStateToProps, mapDispatchToProps)(FacilityMetrics);
